import React from "react";
import { handleUrlOnClick } from "@gettactic/helpers/src/event-handlers/handleUrlOnClick";
import { useRouter } from "../useRouter";

export interface LinkProps
  extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href"> {
  href: string;
  /** Called after the router navigates. */
  onAfterClick?: () => void;
}

/**
 * An in-app link that navigates with the Next.js router on a plain click; modifier clicks
 * and `target="_blank"` are left to the browser. An `onClick` replaces the navigation.
 */

export const Link: React.FC<LinkProps> = React.forwardRef<
  HTMLAnchorElement,
  LinkProps
>(
  (
    {
      href,
      target,
      rel = "noopener noreferrer",
      children,
      onClick,
      onAfterClick,
      ...props
    },
    ref
  ) => {
    const router = useRouter();

    const handleRouterPush = (event: React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      onAfterClick?.();
      router.push(href);
    };

    return (
      <a
        href={href}
        target={target}
        rel={rel}
        // A new-tab link is left to the browser: routing here would open it in this tab
        onClick={
          target === "_blank"
            ? onClick
            : handleUrlOnClick((onClick as never) || handleRouterPush)
        }
        ref={ref}
        {...props}
      >
        {children}
      </a>
    );
  }
);

Link.displayName = "Link";
